import { useTranslation, Icon, useAnimatedClass } from '@testsigma/atoms';
import sync_started from '@images/sync_stated.svg';
import { useConnectionSyncSuccessful } from '../../hooks/use-sync-successful';
import { useEffect } from 'react';
import { MetadataConnectionModalScreens } from '@contexts/sf-connection-context';
import { useContext } from 'react';
import { UserPreferenceContext } from '@contexts/user-preference-context';
import { useSFConnectionState } from '@contexts/sf-connection-context';
import { useGlobalState } from '@providers/global-context-provider';
import { SalesforceConnectionsService } from '@modules/salesforce/services';
import { logErrorToSentry } from '@utils/common';
import { useCheckMetadataInitialSyncStatus } from '../../hooks/use-check-initial-sync-status';

const MetadataSyncPartiallySuccessful: React.FC = () => {
  const { t } = useTranslation();
  const { handleCloseOnPartiallySuccess } = useConnectionSyncSuccessful();
  const { userPreference } = useContext(UserPreferenceContext);
  const projectPreference = userPreference?.preference?.projectPreference;

  const { handleUpdateMetadataConnectionModalScreen, selectedConnection } =
    useSFConnectionState();

  const {
    handleCustomObjectSyncing,
    handleStartRefreshingMetadata,
    handleStopRefreshingMetadata,
  } = useGlobalState();
  const { applicationVersionId, applicationId } = selectedConnection;
  const handleElementCreation = async () => {
    if (!applicationVersionId) {
      return;
    }
    try {
      handleUpdateMetadataConnectionModalScreen(
        MetadataConnectionModalScreens.METADATA_SYNC_DATA_PREPARATION_SCREEN,
      );
      await SalesforceConnectionsService.updateVersionId({
        applicationVersionId,
        applicationId: applicationId || projectPreference?.applicationId,
        connection: selectedConnection,
      });
    } catch (error) {
      logErrorToSentry({ error });
    }
  };

  const { handleStartInitialSyncPolling } = useCheckMetadataInitialSyncStatus({
    onSuccess: () => {
      handleElementCreation();
      handleUpdateMetadataConnectionModalScreen(
        MetadataConnectionModalScreens.METADATA_SYNC_SUCCESSFUL_SCREEN,
      );
      handleCustomObjectSyncing(false);
      handleStopRefreshingMetadata(selectedConnection?.id);
    },
    onFailure: () => {
      handleUpdateMetadataConnectionModalScreen(
        MetadataConnectionModalScreens.METADATA_SYNC_FAILURE_SCREEN,
      );
      handleCustomObjectSyncing(false);
      handleStopRefreshingMetadata(selectedConnection?.id);
    },
    onPartiallySuccess: () => {
      handleCustomObjectSyncing(true);
      handleStartRefreshingMetadata(selectedConnection?.id);
    },
  });

  useEffect(() => {
    handleStartInitialSyncPolling();
    handleCustomObjectSyncing(true);
  }, [handleStartInitialSyncPolling, handleCustomObjectSyncing]);

  return (
    <div className='flex flex-col pt-4 pb-6'>
      <Icon
        className='pr-3 text-xl cursor-pointer text-secondary-600 shrink-0 font-semibold self-end'
        onClick={handleCloseOnPartiallySuccess}
        name={'close'}
      />
      <div className='flex flex-col items-center justify-center h-full !px-10'>
        <div className='leading-8 font-semibold text-secondary-1000 text-2xl'>
          {t('header.metadata_sync_partially_successful')}
        </div>

        <div className='mt-6 mb-8'>
          <img
            src={sync_started}
            className='empty-illustration'
            alt="Metadata sync in progress illustration"
          />
        </div>

        <div
          className='flex mb-10 text-center text-sm text-secondary-text'
          dangerouslySetInnerHTML={{
            __html: t('message.metadata_sync_partially_successful'),
          }}
        />
      </div>
    </div>
  );
};

export { MetadataSyncPartiallySuccessful };
